"use client";

import React, { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from 'next/navigation';
import { generateServiceFromPathname, shouldShowServiceDropdown, REQUIRED_SERVICE } from "@/types/constants";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import { QuoteForm } from "@/.storyblok/types/319751/storyblok-components";
import { renderRichText } from "@/lib/richText";
import { logger } from '@/lib/logger';

interface FormError {
  field: string;
  message: string;
}

interface QuoteFormData {
  name: string;
  email_address: string;
  company: string;
  phone_number: string;
  service: string | null;
  how_did_you_find_us: string;
  enquiry: string;
  marketing: boolean;
  analytics_id: string;
  "WC GCLID": string;
}

interface QuoteFormProps {
  blok?: QuoteForm & SbBlokData;
}

export default function QuoteFormComponent({ blok }: QuoteFormProps) {
  // All hooks must be called at the top level, before any conditional logic
  const [formData, setFormData] = useState<QuoteFormData>({
    name: "",
    email_address: "",
    company: "",
    phone_number: "",
    service: null,
    how_did_you_find_us: "",
    enquiry: "",
    marketing: false,
    analytics_id: "GTM-TFKVPCV",
    "WC GCLID": "",
  });

  const [errors, setErrors] = useState<FormError[]>([]);
  const [submissionError, setSubmissionError] = useState<string>("");
  const pathname = usePathname();
  const currentService = generateServiceFromPathname(pathname);
  const showServiceDropdown = shouldShowServiceDropdown(pathname);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [csrfToken, setCsrfToken] = useState<string>('');

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      service: currentService
    }));
  }, [currentService]);

  useEffect(() => {
    fetch('/api/csrf')
      .then(res => {
        const token = res.headers.get('x-csrf-token');
        if (token) {
          setCsrfToken(token);
        }
      })
      .catch(console.error);
  }, []);


  useEffect(() => {
    const getGclidParam = () => {
      if (typeof window === 'undefined') return null;
      const match = RegExp('[?&]gclid=([^&]*)').exec(window.location.search);
      return match ? decodeURIComponent(match[1].replace(/\+/g, ' ')) : null;
    };

    const shouldStoreGclid = () => {
      const gclsrc = new URLSearchParams(window.location.search).get('gclsrc');
      return !gclsrc || gclsrc.indexOf('aw') !== -1;
    };

    const gclidParam = getGclidParam();

    if (gclidParam && shouldStoreGclid()) {
      const expiryDate = new Date().getTime() + (90 * 24 * 60 * 60 * 1000);
      const gclidData = {
        value: gclidParam,
        expiryDate: expiryDate
      };
      localStorage.setItem('gclid', JSON.stringify(gclidData));

      setFormData(prev => ({
        ...prev,
        "WC GCLID": gclidParam
      }));
    } else {
      const storedGclid = localStorage.getItem('gclid');
      if (storedGclid) {
        const gclidData = JSON.parse(storedGclid);
        if (new Date().getTime() < gclidData.expiryDate) {
          setFormData(prev => ({
            ...prev,
            "WC GCLID": gclidData.value
          }));
        } else {
          localStorage.removeItem('gclid');
        }
      }
    }
  }, []);


  // Early return after all hooks are defined
  if (!blok) return null;

  const {
    image,
    quote_type,
    title = "Get a fast quote",
    description = "Our expert consultants will get back to you as soon as possible.",
    left_title,
    left_subtitle,
    left_content,
    button_label
  } = blok;

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;
    const inputValue =
      type === "checkbox" ? (e.target as HTMLInputElement).checked : value;

    setFormData((prev) => ({
      ...prev,
      [name]: inputValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);
    setSubmissionError("");
    setIsSubmitting(true);

    // Get browser info
    const userAgent = navigator.userAgent;

    // Prepare form data
    const submissionData = {
      ...formData,
      user_agent: userAgent,
      service: formData.service || "Penetration Testing",
    };

    // For debugging
    logger.log('Submitting form data:', submissionData);

    try {
      const response = await fetch("/api/quote", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
        },
        body: JSON.stringify({
          ...submissionData,
          form_name: "fast-quote",
          form_url: window.location.href,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.errors) {
          setErrors(result.errors);
        } else {
          setSubmissionError(result.message || "An error occurred while submitting the form.");
        }
        return;
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'event': 'form1',
        'enhanced_conversion_data': {
          'email': formData.email_address,
          'phone_number': formData.phone_number || ''
        }
      });

      setFormData({
        name: "",
        email_address: "",
        company: "",
        phone_number: "",
        service: "",
        how_did_you_find_us: "",
        enquiry: "",
        marketing: false,
        analytics_id: "GTM-TFKVPCV",
        "WC GCLID": "",
      });

      // Show success message
      alert("Thank you! Your quote request has been submitted successfully.");

    } catch (error) {
      console.error("Form submission error:", error);
      setSubmissionError("An error occurred while submitting the form. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };


  const renderForm = () => (
    <form onSubmit={handleSubmit} className="fast-quote">
      <div className="row gy-2 mb-3">
        <div className="col-24">
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Your name *"
            required
          />
        </div>
        <div className="col-24">
          <input
            type="email"
            name="email_address"
            value={formData.email_address}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Email address *"
            required
          />
        </div>
        <div className="col-24">
          <input
            type="text"
            name="company"
            value={formData.company}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Company name *"
            required
          />
        </div>
        <div className="col-24">
          <input
            type="tel"
            name="phone_number"
            value={formData.phone_number}
            onChange={handleInputChange}
            className="form-control"
            placeholder="Telephone"
          />
        </div>
        {showServiceDropdown ? (
          <div className="col-24">
            <select
              name="service"
              value={formData.service || ''}
              onChange={handleInputChange}
              className="form-control"
              required
              aria-label="Required service"
              aria-required="true"
              aria-invalid={formData.service === ''}
            >
              <option value="">Required service</option>
              {REQUIRED_SERVICE.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="d-none">
            <input
              type="hidden"
              name="service"
              value={formData.service || ''}
            />
          </div>
        )}
        <div className="col-24">
          <textarea
            name="enquiry"
            value={formData.enquiry}
            onChange={handleInputChange}
            rows={3}
            maxLength={1500}
            className="form-control"
            placeholder="Comments *"
            required
          />
        </div>
        <div className="col-24">
          <p className="mb-0">
            <label className="small text-dark">
              <input
                type="checkbox"
                name="marketing"
                checked={formData.marketing}
                onChange={handleInputChange}
                className="me-2"
              />
              I&apos;d like to receive free cyber and compliance resources, and stay up-to-date with Bulletproof services.{" "}
              <Link href="/privacy-notice" target="_blank" className="ms-2">Privacy policy</Link>
            </label>
          </p>
        </div>
      </div>
      <div>
        <input
          type="hidden"
          name="analytics_id"
          value={formData.analytics_id}
          className="analytics_id"
        />
        <div className="text-center">
          <input
            type="hidden"
            id="gclid_field"
            name="WC GCLID"
            value={formData["WC GCLID"]}
          />
          <button type="submit" className="btn btn-md btn-secondary" disabled={isSubmitting}>
            {isSubmitting ? "Submitting..." : (button_label || "Get my quote")}
          </button>
        </div>
      </div>
    </form>
  );

  if (quote_type === "quote_blue") {
    return (
      <div {...storyblokEditable(blok)} className="brand-section">
        <div className="container py-4 py-md-5" id="getQuoteTop">
          <div className="card card-lighter">
            <div className="row">
              <div
                className="col-24 col-lg-12"
                style={{
                  background: image?.filename ? `url(${image.filename}) center / cover no-repeat` : undefined
                }}
              >
              </div>
              <div className="col-24 col-lg-12 p-4">
                <h2 className="h3 text-primary text-center text-lg-start">
                  {title}
                </h2>
                <p className="text-center text-lg-start text-dark">
                  {description}
                </p>

                <form onSubmit={handleSubmit} className="fast-quote">
                  <div className="row gy-2 mb-3 mb-md-4">
                    <div className="col-24">
                      <input
                        className="form-control"
                        id="quote2_name"
                        placeholder="Your name *"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-24">
                      <input
                        className="form-control"
                        id="quote2_email_address"
                        placeholder="Email address *"
                        type="email"
                        name="email_address"
                        value={formData.email_address}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-24">
                      <input
                        className="form-control"
                        id="quote2_company"
                        placeholder="Company name *"
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div className="col-24">
                      <input
                        className="form-control"
                        id="quote2_phone_number"
                        placeholder="Telephone"
                        type="tel"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                      />
                    </div>
                    {showServiceDropdown ? (
                      <div className="col-24">
                        <select
                          name="service"
                          value={formData.service || ''}
                          onChange={handleInputChange}
                          className="form-control"
                          required
                          aria-label="Required service"
                          aria-required="true"
                          aria-invalid={formData.service === ''}
                        >
                          <option value="">Required service</option>
                          {REQUIRED_SERVICE.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="d-none">
                        <input
                          type="hidden"
                          name="service"
                          value={formData.service || ''}
                        />
                      </div>
                    )}
                    <div className="col-24">
                    <textarea
                      rows={3}
                      maxLength={1500}
                      className="form-control"
                      id="quote2_enquiry"
                      placeholder="Comments *"
                      name="enquiry"
                      value={formData.enquiry}
                      onChange={handleInputChange}
                      required
                    />
                    </div>
                    <div className="col-24">
                      <p className="mb-0">
                        <label className="small text-dark">
                          <input
                            name="marketing"
                            type="checkbox"
                            className="me-2"
                            checked={formData.marketing}
                            onChange={handleInputChange}
                          />
                          I&apos;d like to receive free cyber and compliance resources, and stay up-to-date with Bulletproof services.{" "}
                          <Link target="_blank" href="/privacy-notice">
                            Privacy policy
                          </Link>
                        </label>
                      </p>
                    </div>
                  </div>
                  <div>
                    <input
                      type="hidden"
                      name="analytics_id"
                      value={formData.analytics_id}
                      className="analytics_id"
                    />
                    <input
                      type="hidden"
                      id="gclid_field"
                      name="WC GCLID"
                      value={formData["WC GCLID"]}
                    />
                    <div className="text-center">
                      <button type="submit" className="btn btn-md btn-secondary" disabled={isSubmitting}>
                        {isSubmitting ? "Submitting..." : (button_label || "Get my quote")}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Conditionally render structure based on `quote_type`
  if (quote_type === "get_a_fast") {
    return (
      <div {...storyblokEditable(blok)} className="brand-section quote_form_target">
        <div className="container py-4 py-md-5" id="getQuote">
          <div className="card card-lighter">
            <div className="row">
              <div className="col-24 col-lg-12 bg-dark p-4 d-flex flex-column justify-content-center">
                <h2 className="text-primary text-center text-lg-start">
                  {left_title || "Get a fast penetration test quote"}
                </h2>
                <p className="h3 text-center text-lg-start mb-4 fw-normal text-white">
                  {left_subtitle}
                </p>
                {left_content && (
                  <div className="list-targets lead mb-0"
                       dangerouslySetInnerHTML={{ __html: renderRichText(left_content) }} />
                )}
              </div>
              <div className="col-24 col-lg-12 p-4">
                {renderForm()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (quote_type === "get_a_fast_pen_test") {
    return (
      <div {...storyblokEditable(blok)} className="brand-section">
        <div className="container py-4 py-md-5" id="getQuoteTop">
          <div className="card card-lighter">
            <div className="row">
              <div
                className="col-24 col-lg-12"
                style={{
                  background: `url(${image?.filename || ''}) center / cover no-repeat`
                }}
              ></div>
              <div className="col-24 col-lg-12 p-4">
                <h2 className="h3 text-primary text-center text-lg-start">
                  {title || "Get a fast pen test quote"}
                </h2>
                <p className="text-center text-lg-start text-dark">
                  {description || "One of our expert pen test consultants will get back to you as soon as possible."}
                </p>

                <form onSubmit={handleSubmit} className="fast-quote">
                  <div className="row gy-2 mb-3 mb-md-4">
                    <div className="col-24">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Your name *"
                        id="quote2_name"
                        required
                      />
                    </div>
                    <div className="col-24">
                      <input
                        type="email"
                        name="email_address"
                        value={formData.email_address}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Email address *"
                        id="quote2_email_address"
                        required
                      />
                    </div>
                    <div className="col-24">
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Company name *"
                        id="quote2_company"
                        required
                      />
                    </div>
                    <div className="col-24">
                      <input
                        type="tel"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Telephone"
                        id="quote2_phone_number"
                      />
                    </div>
                    {showServiceDropdown ? (
                      <div className="col-24">
                        <select
                          name="service"
                          value={formData.service || ''}
                          onChange={handleInputChange}
                          className="form-control"
                          required
                          aria-label="Required service"
                          aria-required="true"
                          aria-invalid={formData.service === ''}
                        >
                          <option value="">Required service</option>
                          {REQUIRED_SERVICE.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="d-none">
                        <input
                          type="hidden"
                          name="service"
                          value={formData.service || ''}
                        />
                      </div>
                    )}
                    <div className="col-24">
                    <textarea
                      name="enquiry"
                      value={formData.enquiry}
                      onChange={handleInputChange}
                      rows={3}
                      maxLength={1500}
                      className="form-control"
                      placeholder="Comments *"
                      id="quote2_enquiry"
                      required
                    />
                    </div>
                    <div className="col-24">
                      <p className="mb-0">
                        <label className="small text-dark">
                          <input
                            type="checkbox"
                            name="marketing"
                            checked={formData.marketing}
                            onChange={handleInputChange}
                            className="me-2"
                            id="quote2_marketing"
                          />
                          I&apos;d like to receive free cyber and compliance resources, and stay up-to-date with Bulletproof services.{" "}
                          <Link href="/privacy-notice" target="_blank" className="ms-2">
                            Privacy policy
                          </Link>
                        </label>
                      </p>
                    </div>
                  </div>
                  <div>
                    <input
                      type="hidden"
                      name="analytics_id"
                      value={formData.analytics_id}
                      className="analytics_id"
                    />
                    <input
                      type="hidden"
                      id="gclid_field"
                      name="WC GCLID"
                      value={formData["WC GCLID"]}
                    />
                    <div className="text-center">
                      <button type="submit" className="btn btn-md btn-secondary" disabled={isSubmitting}>
                        {isSubmitting ? "Submitting..." : (button_label || "Get my quote")}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (quote_type === "quote_contact") {
    return (
      <div {...(blok && storyblokEditable(blok))} className="quote-section-light">
        <div className="container py-4 py-md-5" id="getQuote">
          <h2 className="text-center no-toc mb-3">
            {title}
          </h2>
          <p className="lead text-center mb-5">
            {description}
          </p>
          <form
            className="fast-quote form-quote col-24 col-lg-16 mx-auto"
            id="new_quote"
            onSubmit={handleSubmit}
          >
            <div className="row gy-3 mb-3 mb-md-4">
              <div className="col-md-12">
                <label className="form-label" htmlFor="name">
                  Name
                </label>
                <input
                  className="form-control"
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-md-12">
                <label className="form-label" htmlFor="phone_number">
                  Phone number
                </label>
                <input
                  className="form-control"
                  type="tel"
                  name="phone_number"
                  id="phone_number"
                  value={formData.phone_number}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-12">
                <label className="form-label" htmlFor="company">
                  Company name
                </label>
                <input
                  className="form-control"
                  type="text"
                  name="company"
                  id="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-md-12">
                <label className="form-label" htmlFor="email_address">
                  Email address
                </label>
                <input
                  className="form-control"
                  type="email"
                  name="email_address"
                  id="email_address"
                  value={formData.email_address}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="col-md-24">
                <label className="form-label" htmlFor="service">
                  Required service
                </label>
                <select
                  name="service"
                  id="service"
                  className="form-select"
                  value={formData.service || ''}
                  onChange={handleInputChange}
                  required
                  aria-label="Required service"
                  aria-required="true"
                  aria-invalid={formData.service === ''}
                >
                  <option value="">Please select</option>
                  {REQUIRED_SERVICE.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-md-24">
                <label
                  className="form-label"
                  htmlFor="how_did_you_find_us"
                >
                  How did you hear about us?
                </label>
                <input
                  className="form-control"
                  type="text"
                  name="how_did_you_find_us"
                  id="how_did_you_find_us"
                  value={formData.how_did_you_find_us}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-24">
                <label className="form-label" htmlFor="enquiry">
                  Information on your enquiry
                </label>
                <small className="char-limit">(1,500 characters limit)</small>
                <textarea
                  rows={4}
                  maxLength={1500}
                  className="form-control"
                  name="enquiry"
                  id="enquiry"
                  value={formData.enquiry}
                  onChange={handleInputChange}
                />
              </div>
              <div className="col-md-24 gy-3 gy-md-4">
                <p className="text-center mb-1">
                  <label htmlFor="marketing">
                    <input
                      className="me-2"
                      type="checkbox"
                      value="1"
                      name="marketing"
                      id="marketing"
                      checked={formData.marketing}
                      onChange={handleInputChange}
                    />
                    I&#39;d like to receive Bulletproof
                    communications about relevant services and events
                  </label>
                </p>
                <p className="text-center mb-0">
                  For more information about how we collect, process and retain
                  your personal data, please see our{" "}
                  <Link target="_blank" href="/privacy-notice">
                    privacy policy
                  </Link>
                  .
                </p>
              </div>
            </div>
            <div className="text-center">
              <input
                type="hidden"
                name="quote[analytics_id]"
                id="quote_analytics_id"
                value={formData.analytics_id}
              />
              <input
                type="hidden"
                id="gclid_field"
                name="WC GCLID"
                value={formData["WC GCLID"]}
              />
              <button
                type="submit"
                name="commit"
                className="btn btn-lg btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : (button_label || "Get my quote")}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div {...(blok && storyblokEditable(blok))} className="quote-section">
      <div className="quote-section-gallery-grey component-form-image">
        <div className="container py-4 py-md-5" id="getQuote">
          <div className="card card-lighter">
            <div className="row g-0">
              <div
                className="col-24 col-lg-12 component-form-image-img"
                style={{
                  position: "relative",
                  minHeight: "300px",
                }}
              >
                {image && image?.filename && (
                  <Image
                    src={image.filename}
                    alt={image.alt || "Quote form image"}
                    fill
                    style={{ objectFit: "cover" }}
                    sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                    loading="lazy"
                    quality={75}
                  />
                )}
              </div>

              <div className="col-24 col-lg-12 p-4 component-form-image-form">
                {title && (
                  <h2 className="h3 text-primary text-center text-lg-start">
                    {title}
                  </h2>
                )}
                {description && (
                  <p className="mb-4 text-center text-lg-start">
                    {description}
                  </p>
                )}

                {errors.length > 0 && (
                  <div className="d-flex justify-content-center">
                    <div className="alert alert-danger w-100">
                      {errors.map((error, index) => (
                        <p key={index} className="mb-0">
                          {error.message}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                {submissionError && (
                  <div className="d-flex justify-content-center">
                    <div className="alert alert-danger w-100">
                      <p className="mb-0">{submissionError}</p>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="fast-quote">
                  <div className="row gy-2 mb-3 mb-md-4">
                    <div className="col-24 col-lg-12">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Your name *"
                        required
                      />
                    </div>
                    <div className="col-24 col-lg-12">
                      <input
                        type="email"
                        name="email_address"
                        value={formData.email_address}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Email address *"
                        required
                      />
                    </div>
                    <div className="col-24 col-lg-12">
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Company name *"
                        required
                      />
                    </div>
                    <div className="col-24 col-lg-12">
                      <input
                        type="tel"
                        name="phone_number"
                        value={formData.phone_number}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="Telephone"
                      />
                    </div>
                    {showServiceDropdown ? (
                      <div className="col-24">
                        <select
                          name="service"
                          value={formData.service || ''}
                          onChange={handleInputChange}
                          className="form-control"
                          required
                          aria-label="Required service"
                          aria-required="true"
                          aria-invalid={formData.service === ''}
                        >
                          <option value="">Required service</option>
                          {REQUIRED_SERVICE.map((service) => (
                            <option key={service} value={service}>
                              {service}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <div className="d-none">
                        <input
                          type="hidden"
                          name="service"
                          value={formData.service || ''}
                        />
                      </div>
                    )}
                    <div className="col-24">
                      <input
                        type="text"
                        name="how_did_you_find_us"
                        value={formData.how_did_you_find_us}
                        onChange={handleInputChange}
                        className="form-control"
                        placeholder="How did you hear about us?"
                      />
                    </div>
                    <div className="col-24">
                      <textarea
                        name="enquiry"
                        value={formData.enquiry}
                        onChange={handleInputChange}
                        rows={3}
                        maxLength={1500}
                        className="form-control"
                        placeholder="Comments *"
                        required
                      />
                    </div>
                    <div className="col-24">
                      <p className="mb-0">
                        <label className="small text-dark">
                          <input
                            type="checkbox"
                            name="marketing"
                            checked={formData.marketing}
                            onChange={handleInputChange}
                            className="me-2"
                          />
                          I&#39;d like to receive free cyber and compliance
                          resources, and stay up-to-date with Bulletproof
                          services.{" "}
                          <Link href="/privacy-notice" target="_blank">
                            Privacy policy
                          </Link>
                        </label>
                      </p>
                    </div>
                  </div>

                  <div>
                    <input
                      type="hidden"
                      name="analytics_id"
                      value={formData.analytics_id}
                      className="analytics_id"
                    />
                    <input
                      type="hidden"
                      id="gclid_field"
                      name="WC GCLID"
                      value={formData["WC GCLID"]}
                    />
                    <div className="text-center">
                      <button
                        type="submit"
                        className="btn btn-md btn-secondary"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? "Submitting..." : (button_label || "Submit")}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}